// Angular Core and Common Modules
import { APP_INITIALIZER, Injector, NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { Router, RouteReuseStrategy } from "@angular/router";

// Ionic Modules and Services
import {
  IonicModule,
  IonicRouteStrategy,
  NavParams,
  Platform,
} from "@ionic/angular";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";

// Angular HttpClient and Interceptors
import { provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";

// Cordova Plugins
import { TouchID } from "@awesome-cordova-plugins/touch-id/ngx";
import { Device } from "@awesome-cordova-plugins/device/ngx";
import { ScreenOrientation } from "@awesome-cordova-plugins/screen-orientation/ngx";
import { InAppBrowser } from "@awesome-cordova-plugins/in-app-browser/ngx";
import { FileOpener } from "@awesome-cordova-plugins/file-opener/ngx";

// Third-party Modules
import { NgIdleModule } from '@ng-idle/core';

// Application Services
import { AuthenticationService } from "./services/authentication/authentication.service";
import { GlobalErrorHandlerService } from "./services/global-errors/global-error-handler.service";
import { UserSessionService } from "./services/user-session/user-session.service";
import { LogoutService } from "./services/logout/logout.service";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ConfigService } from "./services/config/config.service";
import { IonicStorageModule } from "@ionic/storage-angular";
import { CommonModule, DatePipe } from "@angular/common";
import { AuthGuardService } from "./services/auth-guard/auth-guard.service";
import { NotificationApiService } from "./services/notification-api/notification-api.service";
import { AppstorageTsService } from "./services/appstorage/appstorage.ts.service";
import { MarketingService } from "./services/marketing/marketing.service";

// Shared and Component Modules
import { ServicesSharedModule } from "./services/services-shared.module";
import { ComponentsModule } from "./components/components.module";
import { pagesModule } from "./pages/pages.module";

// Application Routes and Animations
import { customTabAnimation } from "./animation/animation";
import { TabLandingPageModule } from "./tabs/tab-landing/tab-landing.module";
import { COMMON_MODAL_COMPONENT_ROUTES } from "src/library/page-routes.lib";
import { APP_ROUTES } from "./app-routes";
import { NavbarComponent } from "./components/navbar/navbar.component";
import { CatbarComponent } from "./components/catbar/catbar.component";
import { MainComponent } from "./components/main/main.component";
import { SizingComponent } from "./components/sizing/sizing.component";
import { SplashOverlayComponent } from "./components/splash-overlay/splash-overlay.component";
import { AppInitComponent } from "./components/app-init/app-init.component";
import { InitGeoComply } from "./components/init-geocomply/init-geocomply";
import { BottomBarComponent } from "./components/navigation-bar/bottom-bar/bottom-bar.component";

//import { ApplePay } from "@awesome-cordova-plugins/apple-pay/ngx";



const ADD_MODAL_COMPONENT_ROUTES = [];
for (let index = 0; index < COMMON_MODAL_COMPONENT_ROUTES.length; index++) {
  ADD_MODAL_COMPONENT_ROUTES.push(COMMON_MODAL_COMPONENT_ROUTES[index]);
}


function init_app(
  injector: Injector,
  config: ConfigService
) {
  return () =>
    new Promise((resolve) => {
      let routes = [];
      config.isLoaded().then(() => {
        //routes = MOBILE_ROUTES;
        const ALL_MOBILE_ROUTES = [...COMMON_MODAL_COMPONENT_ROUTES, ...APP_ROUTES]
        routes = ALL_MOBILE_ROUTES;
        const router: Router = injector.get(Router);
        router.resetConfig(routes);
        resolve(this);
      })
    });
}

const ionicRootOptions = {
  rippleEffect: false,
  navAnimation: customTabAnimation,
  innerHTMLTemplatesEnabled: true,
}

@NgModule({
  declarations: [
    AppComponent

  ],
  bootstrap: [
    AppComponent
  ],
  imports: [
    AppInitComponent,
    BottomBarComponent,
    BrowserModule,
    AppRoutingModule,
    CatbarComponent,
    CommonModule,
    ComponentsModule,
    FormsModule,
    InitGeoComply,
    IonicModule.forRoot(ionicRootOptions),
    IonicStorageModule.forRoot(),
    MainComponent,
    NavbarComponent,
    NgIdleModule.forRoot(),
    pagesModule,
    ReactiveFormsModule,
    ServicesSharedModule,
    TabLandingPageModule,
    SplashOverlayComponent,
    SizingComponent
  ],
  providers: [
    AppstorageTsService,
    AuthenticationService,
    AuthGuardService,
    CommonModule,
    ConfigService,
    DatePipe,
    Device,
    FileOpener,
    GlobalErrorHandlerService,
    InAppBrowser,
    LogoutService,
    MarketingService,
    NavParams,
    NotificationApiService,
    Platform,
    ScreenOrientation,
    TouchID,
    UserSessionService,
    {
      provide: APP_INITIALIZER,
      useFactory: init_app,
      deps: [Injector, ConfigService, AppstorageTsService, MarketingService, Platform],
      multi: true,
    },
    {
      provide: RouteReuseStrategy,
      useClass: IonicRouteStrategy,
    },
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: 'APPLE_PAY',
      useFactory: (platform: Platform) => {
        if (platform.is('ios')) {
          try {
            // eslint-disable-next-line @typescript-eslint/no-var-requires
            const ApplePay = require('@ionic-native/apple-pay/ngx').ApplePay;
            return new ApplePay();
          } catch (error) {
            return null;
          }
        }
        return null;
      },
      deps: [Platform]
    },
    {
      provide: 'IOS_WEB_AUTH_SESSION',
      useFactory: (platform: Platform) => {
        if (platform.is('ios')) {
          try {
            const { BankConnectAuth } = require('capacitor-plugin-webauth-session');
            return BankConnectAuth;
          } catch {
            return null;
          }
        }
        return null;
      },
      deps: [Platform]
    }
  ]
})
export class AppModule { }
